import { useMemo, useState, type Dispatch, type SetStateAction } from "react";
import { SearchSelect, type SSOption, type SSGroup } from "../ui/SearchSelect";
import { ItemFormModal } from "../items/ItemFormModal";
import { Icon } from "../ui/Icon";
import { trpc } from "../../lib/trpc";
import {
  SKT_YIL_MAX_GUN, SKT_YIL_MIN_GUN, istanbulBugun, sktAlanHatalari, sktDuzenlemeKurali, sktEnErkenGun, sktTarihHatasi,
  type SktAlanHatalari, type SktKuralGirdi, type SktMevcutLot, type SktOrigin,
} from "../../lib/sktKurali";
import "./GoodsReceiptDrawer.css";

export type ItemKind = "stock" | "semi" | "finished";
const KIND_LABELS: [ItemKind, string][] = [["stock", "Hammadde / Sarf"], ["semi", "Yarı Mamul"], ["finished", "Mamul"]];
const NEW_CARD_LABEL: Record<ItemKind, string> = { stock: "Yeni Stok Kartı", semi: "Yeni Yarı Mamul", finished: "Yeni Mamul" };

interface ItemOpt { id: string; kind: string; code: string; name: string; itemTypeId: string | null }

/**
 * Düzenlemede mevcut lotun KAYITLI hâli (receiptDetail'den): SKT kuralının bağlamı (kaynak, kabul günü,
 * kayıtlı üretim/SKT günü) + satırın yerinde güncellenip güncellenemeyeceğini söyleyen ürün/lokasyon.
 */
export interface GRMevcutLot extends SktMevcutLot {
  itemId: string;
  itemKind: ItemKind;
  /** Lotun giriş lokasyonu düzenlemenin lokasyonuyla aynı mı (değilse sunucu lotu yeniden açar). */
  lokasyonAyni: boolean;
}

/**
 * Çok-kalemli mal kabul kalem satırı. `lotId` = mevcut lot (düzenlemede yerinde güncelleme için);
 * `mevcut` = o lotun kayıtlı hâli — SKT kuralı sunucudaki gibi lotun KENDİ bağlamıyla işlesin diye.
 */
export interface GRLine { key: number; lotId?: string; mevcut?: GRMevcutLot; turKey: string; itemId: string; itemKind: ItemKind | ""; qty: string; producedAt: string; expiresAt: string; supplierLot: string; costPerUnit: string }
let SEQ = 1;
export const emptyGRLine = (init?: Partial<GRLine>): GRLine => ({ key: SEQ++, turKey: "", itemId: "", itemKind: "", qty: "", producedAt: "", expiresAt: "", supplierLot: "", costPerUnit: "", ...init });

/** Editörün çalıştığı bağlam: yeni fiş (`create`) ya da mevcut fişin kalem düzenlemesi (`edit`). */
export type GRMode = "create" | "edit";

/**
 * Bir kalem satırının SKT kuralı — editörün alan-altı hatası ile ebeveynin kaydetme kapısı AYNI
 * fonksiyonu kullanır (ayna kuralı). Sunucudaki uçların bağlamını birebir izler:
 *  · Yeni fiş (`inventory.createGoodsReceipt`): fişin giriş nedeni + kabul günü = bugün; her satır denetlenir.
 *  · Düzenleme (`updateReceiptLines`): `sktDuzenlemeKurali` — mevcut lot kendi kaynağı + kabul günüyle,
 *    yeni satır FİŞİN kaynağı + bugünle; değişmeyen mevcut satır denetlenmez.
 * `denetle: false` → sunucu bu satırın tarihlerine bakmaz; istemci de hata göstermez/engellemez.
 */
export function grLineSktKurali(ln: GRLine, ctx: { mode: GRMode; origin: SktOrigin }, bugun: string): { girdi: SktKuralGirdi; denetle: boolean } {
  if (ctx.mode === "create") {
    return { girdi: { expiresAt: ln.expiresAt, producedAt: ln.producedAt, origin: ctx.origin, kabulGunu: bugun }, denetle: true };
  }
  if (ln.lotId && !ln.mevcut) {
    // Beklenmez (enterEdit her lotId'li satıra kayıtlı hâli koyar). Bağlam bilinmiyorsa yalnız yıl
    // kuralı — istemci sunucudan SIKI olamaz; asıl kararı sunucu verir.
    return { girdi: { expiresAt: ln.expiresAt, producedAt: "", origin: null, kabulGunu: null }, denetle: true };
  }
  const m = ln.lotId ? ln.mevcut! : null;
  const yerinde = !!m && m.lokasyonAyni && ln.itemId === m.itemId && ln.itemKind === m.itemKind;
  return sktDuzenlemeKurali({ producedAt: ln.producedAt, expiresAt: ln.expiresAt }, m, { fisOrigin: ctx.origin, bugun, yerinde });
}

/** Satırın İLK SKT ihlali (sunucunun `sktTarihHatasi` metni) ya da null — kaydetme kapısı ve üst bant. */
export function grLineSktHatasi(ln: GRLine, ctx: { mode: GRMode; origin: SktOrigin }, bugun: string): string | null {
  const k = grLineSktKurali(ln, ctx, bugun);
  return k.denetle ? sktTarihHatasi(k.girdi) : null;
}

const HATASIZ: SktAlanHatalari = { producedAt: null, expiresAt: null };

/**
 * Mal kabul KALEM editörü — hem "+Mal Kabul" (yeni) hem detayda "Kalemleri Düzenle" tarafından kullanılır.
 * Ebeveyn `lines` state'ini tutar; bu bileşen tür/ürün seçenek sorgularını + kart UI'sını + "Yeni Kart" modalını yönetir.
 */
export function GoodsReceiptLinesEditor({ lines, setLines, enabled = true, origin = "purchase", mode = "create" }: {
  lines: GRLine[]; setLines: Dispatch<SetStateAction<GRLine[]>>; enabled?: boolean;
  /**
   * Fişin giriş nedeni — satır etiketlerini (açılış stoğunda "Parti / Referans No", fiyat yok) ve
   * SKT kuralını bağlama uydurur. Düzenlemede receiptDetail'in fiş kaynağı verilir: yeni eklenen kalem
   * sunucuda bu kaynakla denetlenir ve yazılır.
   */
  origin?: SktOrigin;
  /** SKT kuralının bağlamı — bkz. `grLineSktKurali`. */
  mode?: GRMode;
}) {
  const itemsQ = trpc.inventory.itemOptions.useQuery(undefined, { enabled, staleTime: 60_000 });
  const typesQ = trpc.settings.itemType.list.useQuery({ includeInactive: false }, { enabled, staleTime: 60_000 });
  const [itemAddFor, setItemAddFor] = useState<number | null>(null);
  /**
   * Tarih hatası alanın altında HEMEN görünür (düzenlemede eski hatalı kayıt da açılır açılmaz) —
   * yalnız ODAKTAKİ alanın kendi hatası odak çıkana kadar bekletilir: `type="date"` yıl hanesine
   * her tuşta değer üretir ("2026" yazarken önce "0002", "0020"…), anında göstermek yazarken
   * sahte "yıl hatalı" uyarısı yakıp söndürürdü. Kaydetme kapısı bu görünürlükten BAĞIMSIZDIR —
   * ebeveyn aynı kuralı (grLineSktHatasi) her durumda uygular.
   */
  const [odak, setOdak] = useState<string | null>(null);
  const bugun = istanbulBugun();
  const birak = (alan: string) => setOdak((o) => (o === alan ? null : o));

  const allItems = (itemsQ.data ?? []) as ItemOpt[];
  const types = (typesQ.data ?? []) as Array<{ id: string; kind: string; code: string; name: string }>;

  const turOptions = useMemo<SSGroup[]>(() => KIND_LABELS.map(([k, lbl]) => ({
    group: lbl,
    items: [
      { value: `kind:${k}`, label: `Tüm ${lbl}` },
      ...types.filter((t) => t.kind === k).map((t) => ({ value: `type:${t.id}`, label: t.name })),
    ],
  })), [types]);

  function filteredItems(turKey: string): ItemOpt[] {
    if (!turKey) return [];
    if (turKey.startsWith("type:")) { const id = turKey.slice(5); return allItems.filter((i) => i.itemTypeId === id); }
    if (turKey.startsWith("kind:")) { const k = turKey.slice(5); return allItems.filter((i) => i.kind === k); }
    return [];
  }
  const createKindOf = (turKey: string): ItemKind => {
    if (turKey.startsWith("kind:")) return turKey.slice(5) as ItemKind;
    if (turKey.startsWith("type:")) return (types.find((t) => t.id === turKey.slice(5))?.kind as ItemKind) ?? "stock";
    return "stock";
  };

  function setLine(key: number, patch: Partial<GRLine>) { setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l))); }
  function addLine() { setLines((ls) => [...ls, emptyGRLine()]); }
  function removeLine(key: number) { setLines((ls) => (ls.length <= 1 ? ls : ls.filter((l) => l.key !== key))); }

  function pickItem(key: number, itemId: string) {
    if (!itemId) { setLine(key, { itemId: "", itemKind: "" }); return; }
    const it = allItems.find((x) => x.id === itemId);
    const ln = lines.find((l) => l.key === key);
    // itemKind: allItems'te varsa oradan (kesin); yoksa (yeni kart / bulunamayan) satırın TÜRÜNDEN türet —
    // ürün zaten o türe göre filtreli seçildiğinden createKindOf kesin doğru → itemKind ASLA boş kalmaz.
    setLine(key, { itemId, itemKind: (it?.kind as ItemKind) || (ln ? createKindOf(ln.turKey) : "stock") });
  }

  /**
   * BİRİM FİYAT yalnız YENİ satın alma fişinde. Açılış stoğunda yok (sayım stoğu satın alma değildir,
   * borç doğurmaz). Düzenlemede de yok: `updateReceiptLines` fiyat almaz — alan görünseydi girilen
   * fiyat sessizce kaybolurdu (bulgu G17); yerine kısa not gösterilir.
   */
  const fiyatAlani = mode === "create" && origin === "purchase";
  const acilis = origin === "opening_stock";

  return (
    <>
      <div className="gr-lines-head">
        <div className="gr-lines-title-wrap">
          <span className="gr-lines-title">Kalemler <span className="mono">({lines.length})</span></span>
          {mode === "edit" ? (
            <span className="gr-lines-note"><Icon name="info" size={12} className="gr-lines-note-ic" />Fiyat bu ekrandan düzenlenmez.</span>
          ) : null}
        </div>
        <button type="button" className="btn-md secondary gr-lines-add" onClick={addLine}>
          <Icon name="plus" size={14} />Kalem Ekle
        </button>
      </div>

      <div className="gr-lines-wrap">
        {lines.map((ln, idx) => {
          const opts: SSOption[] = filteredItems(ln.turKey).map((o) => ({ value: o.id, label: o.name, tail: o.code }));
          const kural = grLineSktKurali(ln, { mode, origin }, bugun);
          const sktGirdi = kural.girdi;
          const hata = kural.denetle ? sktAlanHatalari(sktGirdi) : HATASIZ;
          const pAlan = `${ln.key}:p`, sAlan = `${ln.key}:s`;
          const pHata = hata.producedAt && odak !== pAlan ? hata.producedAt : null;
          const sHata = hata.expiresAt && odak !== sAlan ? hata.expiresAt : null;
          const pErrId = `gr-p-err-${ln.key}`, sErrId = `gr-s-err-${ln.key}`;
          return (
            <div key={ln.key} className="gr-line">
              <div className="gr-line-head">
                <span className="gr-line-no">Kalem {idx + 1}</span>
                <button type="button" className="gr-line-del" onClick={() => removeLine(ln.key)} disabled={lines.length <= 1} aria-label={`Kalem ${idx + 1} sil`} title="Kalemi sil"><Icon name="trash" size={14} /></button>
              </div>
              <div className="gr-line-r1">
                <div className="form-field">
                  <label className="lbl">Tür <span className="req">*</span></label>
                  <SearchSelect value={ln.turKey || null} options={turOptions} onChange={(v) => setLine(ln.key, { turKey: v ?? "", itemId: "", itemKind: "" })} placeholder="— Tür seçin —" searchPlaceholder="Tür ara…" />
                </div>
                <div className="form-field">
                  <label className="lbl">Ürün <span className="req">*</span></label>
                  <div className="gr-uc">
                    <SearchSelect value={ln.itemId || null} options={opts} onChange={(v) => pickItem(ln.key, v ?? "")} placeholder={ln.turKey ? "— Ürün seçin —" : "Önce tür seçin"} searchPlaceholder="Ürün adı / kodu ara…" />
                    <button type="button" className="gr-newbtn" title={ln.turKey ? `+ ${NEW_CARD_LABEL[createKindOf(ln.turKey)]}` : "Önce tür seçin"} disabled={!ln.turKey} onClick={() => setItemAddFor(ln.key)} aria-label="Yeni ürün kartı"><Icon name="plus" size={15} /></button>
                  </div>
                </div>
              </div>
              <div className={`gr-line-r2${fiyatAlani ? " gr-r2-5" : ""}`}>
                <div className="form-field">
                  <label className="lbl">Miktar <span className="req">*</span></label>
                  <input className="form-input gr-num" type="number" step="any" min={0} value={ln.qty} onChange={(e) => setLine(ln.key, { qty: e.target.value })} inputMode="decimal" placeholder="0" aria-label={`Kalem ${idx + 1} miktar`} />
                </div>
                <div className={`form-field${pHata ? " gr-has-err" : ""}`}>
                  <label className="lbl">Üretim Tarihi</label>
                  <input className={`form-input gr-mono${pHata ? " err" : ""}`} type="date" value={ln.producedAt}
                    min={SKT_YIL_MIN_GUN} max={SKT_YIL_MAX_GUN}
                    onChange={(e) => setLine(ln.key, { producedAt: e.target.value })}
                    onFocus={() => setOdak(pAlan)} onBlur={() => birak(pAlan)}
                    aria-invalid={pHata ? true : undefined} aria-describedby={pHata ? pErrId : undefined}
                    aria-label={`Kalem ${idx + 1} üretim tarihi`} />
                  {pHata ? <span id={pErrId} className="err gr-date-err" role="alert"><Icon name="alertTriangle" size={12} className="gr-date-err-ic" />{pHata}</span> : null}
                </div>
                <div className={`form-field${sHata ? " gr-has-err" : ""}`}>
                  <label className="lbl">Son Kullanma (SKT)</label>
                  {/* min: üretim tarihi / (satın almada) kabul gününün ERTESİ — takvim öncesini seçtirmez; elle yazılan değeri kural yakalar. */}
                  <input className={`form-input gr-mono${sHata ? " err" : ""}`} type="date" value={ln.expiresAt}
                    min={sktEnErkenGun(sktGirdi)} max={SKT_YIL_MAX_GUN}
                    onChange={(e) => setLine(ln.key, { expiresAt: e.target.value })}
                    onFocus={() => setOdak(sAlan)} onBlur={() => birak(sAlan)}
                    aria-invalid={sHata ? true : undefined} aria-describedby={sHata ? sErrId : undefined}
                    aria-label={`Kalem ${idx + 1} SKT`} />
                  {sHata ? <span id={sErrId} className="err gr-date-err" role="alert"><Icon name="alertTriangle" size={12} className="gr-date-err-ic" />{sHata}</span> : null}
                </div>
                <div className="form-field">
                  {/* Açılış stoğunda TEDARİKÇİ YOK — başlıkta tedarikçi alanı gizleniyor ama
                      satırda "Tedarikçi Parti No" istemek çelişkili olurdu. Alan KALIR (sayımda
                      raf/palet etiketi girilir), yalnız ADI bağlama uyar. */}
                  <label className="lbl">{acilis ? "Parti / Referans No" : "Tedarikçi Parti No"}</label>
                  <input className="form-input" type="text" value={ln.supplierLot} onChange={(e) => setLine(ln.key, { supplierLot: e.target.value })} maxLength={100} placeholder={acilis ? "Raf / palet no" : "SUP-…"} aria-label={`Kalem ${idx + 1} parti no`} />
                </div>
                {/* BİRİM FİYAT — tedarikçi cari hesabına yazılacak tutarın kaynağı (bkz. fiyatAlani). */}
                {fiyatAlani ? (
                  <div className="form-field">
                    <label className="lbl">Birim Fiyat</label>
                    <input className="form-input gr-num" inputMode="decimal" value={ln.costPerUnit}
                      onChange={(e) => setLine(ln.key, { costPerUnit: e.target.value })}
                      placeholder="0,00" aria-label={`Kalem ${idx + 1} birim fiyat`} />
                  </div>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>

      <ItemFormModal
        open={itemAddFor != null}
        kind={itemAddFor != null ? createKindOf(lines.find((l) => l.key === itemAddFor)?.turKey ?? "") : "stock"}
        presetItemTypeId={(() => { const tk = lines.find((l) => l.key === itemAddFor)?.turKey ?? ""; return tk.startsWith("type:") ? tk.slice(5) : undefined; })()}
        onClose={() => setItemAddFor(null)}
        onCreated={(it) => { if (itemAddFor != null) pickItem(itemAddFor, it.id); }}
      />
    </>
  );
}
